import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import { ChartTooltip } from "../ChartTooltip";
import { DocumentReviewSummary } from "../DocumentReviewSummary";
import {
  EVENT_PIN_HEAD_GAP,
  EVENT_PIN_HEIGHT,
  EventPin,
  EventPinLabel,
  eventLabelWidth,
} from "../EventPin";
import { EventSummary } from "../EventSummary";
import {
  documentLabelOpacity,
  endOfReviewWindow,
  formatDate,
  formatReviewPeriod,
  interpolateDomain,
  isInDomain,
  lerp,
  placeEventPins,
  reviewLegs,
  reviewPeriodDomain,
  reviewWindowStatusOf,
  timeScale,
  weekTicks,
  yearLabelOpacity,
  type DocumentReview,
  type ReviewWindow,
  type ReviewWindowStatus,
  type TimeDomain,
} from "../helpers";
import {
  ReviewWindowStatusLegend,
  useWindowStatusLabel,
} from "../ReviewWindowStatusLegend";
import { ReviewWindowSummary } from "../ReviewWindowSummary";
import { useText } from "../text";
import { type AnnualWheelEvent, type ReviewWindowsChartProps } from "../types";
import { useChartTooltip } from "../useChartTooltip";
import { useElementWidth } from "../useElementWidth";
import { workflowStepColor } from "../WorkflowStepSwatch";
import {
  HORIZON_ZOOM_STEP,
  panHorizon,
  stackEventLabels,
  timelineTicks,
  zoomHorizon,
  type TimelineTick,
} from "./timelineHelpers";
import { usePanZoom } from "./usePanZoom";

const TICK_LABELS_HEIGHT = 28;
// The event pins stand in a row of their own, on top of the lanes
const EVENT_PINS_HEIGHT = 28;
// Above the pins, each of their labels takes the lowest of these rows with room for it
const MAX_EVENT_LABEL_ROWS = 5;
const EVENT_LABEL_ROW_HEIGHT = 16;
// Between one label and the next in the same row
const EVENT_LABEL_GAP = 8;
// A narrower timeline has room to label only a few of its pins
const MIN_LABELLED_WIDTH = 640;
// Labels on only some of the pins say little, so they wait until most have room
const MIN_LABELLED_SHARE = 0.75;
const LANE_HEIGHT = 36;
const DOCUMENT_ROW_HEIGHT = 28;
const NAME_COLUMN_WIDTH = 224;
const STATUS_COLUMN_WIDTH = 96;
const MAX_WEEK_TICKS = 10;
const LEG_GAP = 2;
// A version label shows only where the next review leaves it room, in pixels
const VERSION_LABEL_ROOM = 72;
const MIN_DOCUMENTS_WIDTH = 896;

// The same colours as the arcs of the wheel, as backgrounds with their text
const windowStatusBackground: Record<ReviewWindowStatus, string> = {
  done: "bg-secondary text-secondary-foreground",
  ongoing: "bg-primary text-primary-foreground",
  upcoming: "bg-info text-primary-foreground",
};

interface TicksProps {
  ticks: TimelineTick[];
  x: (date: Date) => number;
  opacity: number;
}

const TimelineTicks = ({ ticks, x, opacity }: TicksProps) => (
  <div
    className="absolute inset-0 overflow-hidden"
    style={{ opacity }}
    aria-hidden
  >
    {ticks.map(({ date, label }) => (
      <div
        key={date.getTime()}
        className="border-border text-muted-foreground absolute inset-y-0 border-l pl-2 text-xs whitespace-nowrap"
        style={{ left: `${x(date)}%` }}
      >
        {label}
      </div>
    ))}
  </div>
);

interface Props extends ReviewWindowsChartProps {
  // What the timeline shows while no window is opened
  horizon: TimeDomain;
  onHorizonChange: (update: (horizon: TimeDomain) => TimeDomain) => void;
  // The review windows are then the reviews of one document, one per release
  isDocumentHistory: boolean;
}

export const ReviewWindowsLine = ({
  today,
  reviewWindows,
  openedWindow,
  zoom,
  onOpen,
  events,
  highlightedEventId,
  onEventOpen,
  horizon,
  onHorizonChange,
  isDocumentHistory,
}: Props) => {
  const { t } = useTranslation();
  const text = useText();
  const windowStatusLabel = useWindowStatusLabel();
  const windowTooltip = useChartTooltip<ReviewWindow>();
  const documentTooltip = useChartTooltip<DocumentReview>();
  const eventTooltip = useChartTooltip<AnnualWheelEvent>();

  const openedDomain = openedWindow && reviewPeriodDomain(openedWindow);
  const domain = openedDomain
    ? interpolateDomain(horizon, openedDomain, zoom)
    : horizon;
  const x = timeScale(domain, [0, 100]);

  const isPannable = zoom === 0;
  const panZoom = usePanZoom<HTMLDivElement>({
    isEnabled: isPannable,
    zoomStep: HORIZON_ZOOM_STEP,
    onPan: (fraction) =>
      onHorizonChange((current) => panHorizon(current, fraction)),
    onZoom: (factor, anchor) =>
      onHorizonChange((current) => zoomHorizon(current, factor, anchor)),
  });
  const width = useElementWidth(panZoom.ref);

  const laneCount = Math.max(
    1,
    ...reviewWindows.map((reviewWindow) => reviewWindow.lane + 1),
  );
  const documentReviews = openedWindow?.documentReviews ?? [];
  const activeDocumentReview = documentTooltip.active?.datum;
  const isDimmed = (documentReview: DocumentReview) =>
    activeDocumentReview !== undefined &&
    activeDocumentReview !== documentReview;

  const eventLabelsHeight =
    width >= MIN_LABELLED_WIDTH
      ? MAX_EVENT_LABEL_ROWS * EVENT_LABEL_ROW_HEIGHT
      : 0;
  const eventsHeight = eventLabelsHeight + EVENT_PINS_HEIGHT;
  const headerHeight = TICK_LABELS_HEIGHT + eventsHeight;
  const domainStart = domain[0].getTime();
  // Not clamped like x: a label takes its row from where its pin is, in view or not
  const pinPosition = (date: Date) =>
    ((date.getTime() - domainStart) / (domain[1].getTime() - domainStart)) *
    width;
  const eventPins = placeEventPins(
    events,
    domain,
    pinPosition,
    EVENT_PIN_HEAD_GAP,
    EVENT_PIN_HEIGHT,
  );
  const stackedEventLabels = stackEventLabels(
    events,
    pinPosition,
    (annualWheelEvent) => eventLabelWidth(annualWheelEvent) + EVENT_LABEL_GAP,
    width,
    MAX_EVENT_LABEL_ROWS,
  );
  const showEventLabels =
    eventLabelsHeight > 0 &&
    stackedEventLabels.size >= events.length * MIN_LABELLED_SHARE;
  const eventLabels = showEventLabels ? stackedEventLabels : undefined;
  const eventLabelY = (row: number) =>
    eventLabelsHeight - (row + 0.5) * EVENT_LABEL_ROW_HEIGHT;

  const lanesHeight = lerp(
    laneCount * LANE_HEIGHT,
    documentReviews.length * DOCUMENT_ROW_HEIGHT,
    zoom,
  );
  const height = headerHeight + lanesHeight;
  const showToday = isInDomain(today, domain);

  return (
    <>
      {/* A document's own reviews are coloured by workflow step instead */}
      {!isDocumentHistory && (
        <ReviewWindowStatusLegend opacity={yearLabelOpacity(zoom)} />
      )}
      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: zoom * MIN_DOCUMENTS_WIDTH }}>
          <ul
            className="shrink-0 overflow-hidden"
            style={{
              width: zoom * NAME_COLUMN_WIDTH,
              height,
              paddingTop: headerHeight,
              opacity: zoom,
            }}
            inert={zoom < 1}
          >
            {documentReviews.map((documentReview) => (
              <li
                key={documentReview.entry.id}
                tabIndex={0}
                className={cn(
                  "focus-visible:ring-ring flex h-7 w-56 items-center rounded-sm pr-3 text-sm transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-inset",
                  isDimmed(documentReview) && "opacity-30",
                )}
                {...documentTooltip.markProps(documentReview)}
              >
                <span className="truncate">{documentReview.entry.name}</span>
              </li>
            ))}
          </ul>

          <div className="relative min-w-0 flex-1 pb-7">
            <div
              {...panZoom}
              role="group"
              aria-label={text.view.line}
              tabIndex={isPannable ? 0 : undefined}
              className={cn(
                "relative rounded-sm outline-none",
                isPannable &&
                  "focus-visible:ring-ring cursor-grab touch-pan-y select-none focus-visible:ring-2 focus-visible:ring-inset active:cursor-grabbing",
              )}
              style={{ height }}
            >
              <TimelineTicks
                ticks={timelineTicks(horizon, width).filter(({ date }) =>
                  isInDomain(date, domain),
                )}
                x={x}
                opacity={yearLabelOpacity(zoom)}
              />
              {openedDomain && (
                <TimelineTicks
                  ticks={weekTicks(openedDomain, MAX_WEEK_TICKS).map(
                    (date) => ({ date, label: formatDate(date, "dayMonth") }),
                  )}
                  x={x}
                  opacity={documentLabelOpacity(zoom)}
                />
              )}
              <div className="border-border absolute inset-y-0 right-0 border-l" />

              {zoom < 1 && (
                <div
                  className="absolute inset-x-0"
                  style={{ top: headerHeight, opacity: 1 - zoom }}
                  inert={zoom > 0}
                >
                  {reviewWindows.map((reviewWindow, index) => {
                    const count = reviewWindow.documentReviews.length;
                    const left = x(reviewWindow.reviewFrom);
                    const right = x(endOfReviewWindow(reviewWindow.reviewTo));
                    const documents = text.documents(count);
                    const status = reviewWindowStatusOf(reviewWindow, today);
                    const [documentReview] = reviewWindow.documentReviews;

                    if (right <= left) return null;

                    if (isDocumentHistory && documentReview) {
                      const version = t(
                        ($) => $.commons.workflow.versionOrder,
                        { order: documentReview.release.majorVersion },
                      );
                      const nextReviewFrom =
                        reviewWindows[index + 1]?.reviewFrom;
                      const labelRoom =
                        (((nextReviewFrom ? x(nextReviewFrom) : 100) - left) /
                          100) *
                        width;

                      return (
                        <button
                          key={reviewWindow.reviewFrom.getTime()}
                          type="button"
                          aria-label={`${version}: ${formatReviewPeriod(reviewWindow)}`}
                          className="group focus-visible:ring-ring absolute h-9 cursor-pointer rounded-sm outline-none focus-visible:ring-2"
                          style={{
                            left: `${left}%`,
                            width: `${right - left}%`,
                            top: reviewWindow.lane * LANE_HEIGHT,
                          }}
                          onClick={() => onOpen(reviewWindow)}
                          {...documentTooltip.markProps(documentReview)}
                        >
                          {reviewLegs(documentReview).map((leg, order) => {
                            const legLeft = x(leg.from);
                            const legRight = x(leg.to);

                            if (legRight <= legLeft) return null;

                            return (
                              <span
                                key={order}
                                className={cn(
                                  "absolute top-1 h-4 min-w-px rounded-xs bg-current transition-opacity group-hover:opacity-80",
                                  leg.workflowStep
                                    ? workflowStepColor(order)
                                    : "text-foreground/10",
                                )}
                                style={{
                                  left: `${((legLeft - left) / (right - left)) * 100}%`,
                                  width: `calc(${((legRight - legLeft) / (right - left)) * 100}% - ${LEG_GAP}px)`,
                                }}
                              />
                            );
                          })}
                          {labelRoom >= VERSION_LABEL_ROOM && (
                            <span className="text-muted-foreground absolute top-5 left-0 text-xs whitespace-nowrap">
                              {version}
                            </span>
                          )}
                        </button>
                      );
                    }

                    return (
                      <button
                        key={reviewWindow.reviewFrom.getTime()}
                        type="button"
                        aria-label={`${formatReviewPeriod(reviewWindow)}: ${documents}, ${windowStatusLabel[status]}`}
                        className={cn(
                          "focus-visible:ring-ring @container absolute flex h-7 cursor-pointer items-center gap-1.5 overflow-hidden rounded-sm px-2 text-xs transition-opacity outline-none hover:opacity-80 focus-visible:ring-2",
                          windowStatusBackground[status],
                        )}
                        style={{
                          left: `${left}%`,
                          width: `${right - left}%`,
                          top: reviewWindow.lane * LANE_HEIGHT,
                        }}
                        onClick={() => onOpen(reviewWindow)}
                        {...windowTooltip.markProps(reviewWindow)}
                      >
                        <span className="hidden text-sm font-semibold @[1.125rem]:inline">
                          {count}
                        </span>
                        <span className="hidden @[5rem]:inline">
                          {text.documentUnit(count)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {zoom > 0 && (
                <div
                  className={cn(
                    "absolute inset-x-0",
                    zoom < 1 && "pointer-events-none",
                  )}
                  style={{ top: headerHeight, opacity: zoom }}
                >
                  {documentReviews.map((documentReview, index) => (
                    <div
                      key={documentReview.entry.id}
                      className={cn(
                        "absolute inset-x-0 h-7 transition-opacity",
                        isDimmed(documentReview) && "opacity-30",
                      )}
                      style={{ top: index * DOCUMENT_ROW_HEIGHT }}
                      {...documentTooltip.markProps(documentReview)}
                    >
                      {reviewLegs(documentReview).map((leg, order) => {
                        const left = x(leg.from);
                        const right = x(leg.to);

                        if (right <= left) return null;

                        return (
                          <div
                            key={order}
                            className={cn(
                              "absolute top-1.5 h-4 rounded-xs bg-current",
                              leg.workflowStep
                                ? workflowStepColor(order)
                                : "text-foreground/10",
                            )}
                            style={{
                              left: `${left}%`,
                              width: `calc(${right - left}% - ${LEG_GAP}px)`,
                            }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              <svg
                className="absolute inset-x-0 w-full overflow-visible"
                style={{ top: TICK_LABELS_HEIGHT, height: eventsHeight }}
              >
                {eventPins.map(({ annualWheelEvent, tip, head }) => {
                  const label = eventLabels?.get(annualWheelEvent.id);

                  return (
                    <EventPin
                      key={annualWheelEvent.id}
                      annualWheelEvent={annualWheelEvent}
                      tip={[tip, eventsHeight]}
                      head={[head, eventsHeight - EVENT_PIN_HEIGHT]}
                      guideEnd={[tip, eventsHeight + lanesHeight]}
                      labelPoint={label && [head, eventLabelY(label.row)]}
                      isHighlighted={annualWheelEvent.id === highlightedEventId}
                      tooltip={eventTooltip}
                      onOpen={() => onEventOpen(annualWheelEvent)}
                    />
                  );
                })}
                {eventPins.map(({ annualWheelEvent, head }) => {
                  const label = eventLabels?.get(annualWheelEvent.id);

                  return (
                    label && (
                      <EventPinLabel
                        key={annualWheelEvent.id}
                        annualWheelEvent={annualWheelEvent}
                        point={[head, eventLabelY(label.row)]}
                        isFlipped={label.isFlipped}
                        tooltip={eventTooltip}
                        onOpen={() => onEventOpen(annualWheelEvent)}
                      />
                    )
                  );
                })}
              </svg>
              {showToday && (
                <div
                  className="bg-foreground pointer-events-none absolute bottom-0 w-0.5 -translate-x-1/2"
                  style={{ left: `${x(today)}%`, top: headerHeight }}
                />
              )}
            </div>

            {showToday && (
              <div
                className="bg-foreground text-background absolute bottom-0 rounded-sm px-2 py-0.5 text-xs whitespace-nowrap"
                // Centred on today in the middle, sliding to stay inside towards the edges
                style={{
                  left: `${x(today)}%`,
                  transform: `translateX(-${x(today)}%)`,
                }}
              >
                {`${text.today} · ${formatDate(today, "dayMonth")}`}
              </div>
            )}
          </div>

          <ul
            className="shrink-0 overflow-hidden"
            style={{
              width: zoom * STATUS_COLUMN_WIDTH,
              height,
              paddingTop: headerHeight,
              opacity: zoom,
            }}
            aria-hidden
          >
            {documentReviews.map((documentReview) => (
              <li
                key={documentReview.entry.id}
                className={cn(
                  "text-muted-foreground flex h-7 w-24 items-center justify-end text-xs whitespace-nowrap transition-opacity",
                  isDimmed(documentReview) && "opacity-30",
                )}
              >
                {t(($) => $.commons.workflow[documentReview.release.status])}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {windowTooltip.active && (
        <ChartTooltip x={windowTooltip.active.x} y={windowTooltip.active.y}>
          <ReviewWindowSummary
            reviewWindow={windowTooltip.active.datum}
            statusLabel={
              windowStatusLabel[
                reviewWindowStatusOf(windowTooltip.active.datum, today)
              ]
            }
          />
        </ChartTooltip>
      )}
      {documentTooltip.active && (
        <ChartTooltip x={documentTooltip.active.x} y={documentTooltip.active.y}>
          <DocumentReviewSummary
            documentReview={documentTooltip.active.datum}
          />
        </ChartTooltip>
      )}
      {eventTooltip.active && (
        <ChartTooltip x={eventTooltip.active.x} y={eventTooltip.active.y}>
          <EventSummary annualWheelEvent={eventTooltip.active.datum} />
        </ChartTooltip>
      )}
    </>
  );
};
